import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createOrder,
  fetchAllAgorders,
  updateAgorder,
  fetchOneAgorder,
  deleteAgorder,
  fetchLastOrderNumber,
  generateOrderHistoryReport,
  generateOrderHistoryActualReport,
  generateOrderRegisterReport,
  fetchOrderPrintFormats,
  generateOrderPrint,
  generatePendingOrdersReport,
} from "services/agorder";


export const useAllAgorders = (filters = {}, enabled = true) => {
  const query = useQuery({
    queryKey: ["all-agorders", filters],
    queryFn: () => fetchAllAgorders(filters),
    enabled
  });
  return query;
};

export const useOneAgorder = (id, enabled = true, options = {}) => {
  const query = useQuery({
    queryKey: ["one-agorder", id, options],
    queryFn: () => {
      if (id) {
        return fetchOneAgorder(id, options);
      }
    },
    enabled: enabled && !!id,
  });
  return query;
};

export const useCreateOrder = () => {
  const queryClient = useQueryClient();
  
  const mutation = useMutation({
    mutationFn: (orderData) => createOrder(orderData),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["all-agorders"] });
      queryClient.invalidateQueries({ queryKey: ["last-order-number"] });
    },
  });
  
  return mutation;
};

export const useUpdateAgorder = () => {
  const queryClient = useQueryClient();
  
  const query = useMutation({
    mutationFn: ({ id, data }) => updateAgorder({id, data}),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["all-agorders"] });
      queryClient.invalidateQueries({ queryKey: ["one-agorder", variables.id] });
    },
  });
  return query;
};

export const useDeleteAgorder = () => {
  const queryClient = useQueryClient();
  
  const query = useMutation({
    mutationFn: (id) => deleteAgorder(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ["all-agorders"] });
      queryClient.invalidateQueries({ queryKey: ["one-agorder", id] });
    },
  });
  return query;
};

export const useLastOrderNumber = (ordertype = '', enabled = true) => {
  const query = useQuery({
    queryKey: ["last-order-number", ordertype],
    queryFn: () => fetchLastOrderNumber(ordertype),
    enabled
  });
  return query;
};

export const useGenerateOrderHistoryReport = () => {
  return useMutation({
    mutationFn: (reportData) => generateOrderHistoryReport(reportData),
  });
};

export const useGenerateOrderHistoryActualReport = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateOrderHistoryActualReport(reportData),
  });
  
  return mutation;
};

export const useGenerateOrderRegisterReport = () => {
  const mutation = useMutation({
    mutationFn: (reportData) => generateOrderRegisterReport(reportData),
  });
  
  return mutation;
};

export const useOrderPrintFormats = () => {
  const query = useQuery({
    queryKey: ["order-print-formats"],
    queryFn: () => fetchOrderPrintFormats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  return query;
};

export const useGenerateOrderPrint = () => {
  const mutation = useMutation({
    mutationFn: ({ orderId, formatId, sizeGroupId, withRate }) => generateOrderPrint(orderId, formatId, sizeGroupId, withRate),
  });
  
  return mutation;
};

export const useGeneratePendingOrdersReport = () => {
  return useMutation({
    mutationFn: (reportData) => generatePendingOrdersReport(reportData),
  });
};
